import { useEffect, useState } from 'react';
import styles from './Avatar.module.css';

/**
 * The legacy app always tried a photo first and fell back to a circle with
 * initials (`.avatar-container` / `.avatar-initial`) when none loaded. Now
 * that avatar upload exists (PLAN.md Phase 10.5), `imageUrl` is that photo —
 * a Google account's avatar, or an uploaded one (`SettingsScreen.tsx`). Still
 * falls back to initials whenever there is none, or the URL fails to load
 * (`onError`) — a dead image is exactly as unhelpful as no image.
 */
export default function Avatar({
  name,
  imageUrl,
  size = 44,
}: {
  name: string;
  /** Null/undefined for no photo (most participants, still, until they upload one). */
  imageUrl?: string | null;
  /** Pixel diameter. Legacy varied this per context (roster vs. podium). */
  size?: number;
}) {
  const [failed, setFailed] = useState(false);

  // A long-lived instance (AppHeader's, in particular, never unmounts across
  // navigation) must not stay stuck on initials forever after one dead URL —
  // a fresh `imageUrl` (a new upload replacing a since-deleted one, say)
  // deserves its own attempt, not the previous URL's failure.
  useEffect(() => setFailed(false), [imageUrl]);

  if (imageUrl && !failed) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={styles.avatar}
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    );
  }

  // Matches the legacy fallback exactly: first two characters of the whole
  // name, not per-word initials — "Ana Paula" becomes "AN", not "AP".
  const initials = name.trim().slice(0, 2).toUpperCase() || '?';

  return (
    <div
      className={styles.avatar}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}
